// ============================================================
// VERTEX Performance — Tipos del manual
// ============================================================
// El cleanup `no-explicit-any` de admin (Fases 5.2–5.7a) migró el grueso
// de las interfaces de tablas al archivo generado por Supabase
// (`database.types.ts`). Cada pantalla compone su shape vía
// `Database['public']['Tables']['<tabla>']['Row']` + el embed que pida.
//
// Este manual queda como la **capa que el generado no puede dar** (ADR-0020):
//
//   1. Uniones de CHECK constraints. La BD usa CHECK (no enum nativo) en
//      `profiles.role`, `groups.level`, `sessions.status`, `enrollments.status`,
//      `orders.status`, `session_packs.type`, `session_packs.credit_type`,
//      `products.category`. El generador emite estas columnas como `string` —
//      el manual mantiene la unión literal para que el código diferencie
//      comportamiento por valor (avance de estado de pedido, segmentación
//      por nivel/categoría, etc.).
//
//   2. Interfaces de tablas que el generado no cubre. `news` se mantiene
//      con shape completo aquí porque `NoticiasPage` la consume como entidad
//      (insert/update/delete). El patrón estándar es importar desde el
//      generado; ésta es la excepción restante.
//
// Historial de retiradas:
// - 5.3 (bonos+créditos): `UserCredit`.
// - 5.4a (sesiones+grupos): `Group`, `Session`, `SpecialClass`, `Enrollment`,
//   `SpecialClassEnrollment`, `WaitlistEntry`.
// - 5.5 (tienda): `Product`, `OrderItem`, `Order`.
// - 5.6 (GYM+chat+notif+evaluaciones): `Message`, `Notification`,
//   `StudentEvaluation`, `CustomMetric`, `SessionRating`. Bonus: el panel
//   "Últimas evaluaciones" de `AlumnoDetallePage` dependía del manual
//   `StudentEvaluation` con un modelo pre-033 (`actitud/esfuerzo/motivacion/
//   overall_score/progress_note`) que la BD ya no tiene — el panel se
//   simplificó al schema real, fix funcional documentado en §21.3.
// - 5.7a (cierre admin): `Sede`, `Profile`, `SessionPack`, `AppConfig`,
//   `UserRole`, `SportPosition`, `EnrollResult`, `CancelResult` — sin
//   consumidores tras la migración de los huérfanos.

// Uniones literales CHECK — la BD usa CHECK (no enum) y el generado emite
// estas columnas como `string`.
export type GroupLevel = 'basico' | 'medio' | 'avanzado' | 'tecnificacion';
export type SessionStatus = 'scheduled' | 'completed' | 'cancelled';
export type EnrollmentStatus = 'confirmed' | 'waitlist' | 'cancelled';
// 'expired' entra con la mig 240: un pedido `pending` sin payment intent es un
// carrito abandonado, y a las 24 h el cron lo aparta aquí. No es un pedido
// cancelado —nadie lo canceló— ni uno pendiente —ya no puede pagarse.
export type OrderStatus = 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled' | 'expired';

// [259] El eje de PREPARACION, separado del de cobro. `status` decia las dos
// cosas a la vez y por eso el boton que significa "lo estoy preparando"
// escribia 'paid', que es la palabra del candado de `fulfill_order`.
//
//   sin_cobrar  aun no se ha cobrado, asi que todavia no se sabe si habra
//               preparacion. `has_physical` no existe hasta que corre el reparto.
//   no_aplica   se cobro y no llevaba nada fisico (bonos, matricula, servicios).
//   pendiente   se cobro, lleva algo fisico y nadie lo ha tocado.
//   enviado / entregado
//
// De 'sin_cobrar' no se vuelve nunca: un cobro no se des-hace.
export type PrepStatus = 'sin_cobrar' | 'no_aplica' | 'pendiente' | 'enviado' | 'entregado';

// FASE 1: `status` todavia admite 'shipped' y 'delivered' porque el APK
// instalado los escribe. Para el panel los tres significan lo mismo —cobrado—,
// asi que se colapsan al leer. Desaparece en la fase 2, tras el build.
export function cobroDe(status: OrderStatus): OrderStatus {
  return status === 'shipped' || status === 'delivered' ? 'paid' : status;
}
// Los 19 valores del CHECK `notifications_type_check`, en su orden.
//
// ⚠️ HOY NADIE CONSUME ESTE TIPO. Se ha comprobado: no hay ni un `import` ni
// una anotación que lo use, y por eso llevaba **cinco valores de retraso** —
// le faltaban renewal, subscription_expiry, session_moved, age18_notice y
// child_access_activated, todos escribibles en la base desde hace meses. Se
// completa entero en vez de añadirle solo el de la 309: una lista que miente
// en cinco sitios es peor que no tener lista, porque el primero que la use se
// la creerá. Si alguien la adopta, que la ponga al día contra el CHECK.
export type NotificationType =
  | 'reminder'
  | 'waitlist'
  | 'purchase'
  | 'cancellation'
  | 'broadcast'
  // [mig 309] el aviso de un mensaje. Antes se escribía como 'broadcast' (o
  // como 'admin_alert' si el mensaje iba al club), y eran 172 filas.
  | 'chat'
  | 'evaluation'
  | 'expiry'
  | 'gym_routine'
  | 'gym_feedback'
  | 'enrollment'
  | 'gift'
  | 'news'
  | 'admin_alert'
  | 'renewal'
  | 'subscription_expiry'
  | 'session_moved'
  | 'age18_notice'
  | 'child_access_activated';
export type CreditType = 'grupal' | 'individual' | 'especial' | 'gym';
export type ProductCategory = 'equipacion' | 'camiseta' | 'accesorio';
export type PackType = 'individual' | 'bono';

// Entidad de noticias — usada por `NoticiasPage` como shape completo.
export interface NewsItem {
  id: string;
  title: string;
  summary: string | null;
  body: string;
  image_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  facebook_url: string | null;
  tiktok_url: string | null;
  external_url: string | null;
  published: boolean;
  published_at: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}
